import React, { useState } from "react";
import { useOrder } from "../context/OrderContext";
import { useOrderSave } from "../context/OrderSaveContext";
import TablerLayout from "../components/layout/TablerLayout";
import StepperNavButton from "../components/stepper/StepperNavButton";
import { useStepNavigate } from "../hooks/useStepNavigate";
import FloatingLabelInput from "../components/form_elements/FloatingLabelInput";
import { showErrorToast, showSuccessToast } from "../components/toasts/ToastService";

const decimalPattern = /^\d{0,3}([.,]\d{0,2})?$/;

const formatDecimalInput = (value) => {
    if (
        value === null ||
        value === undefined ||
        value === "null" ||
        value === "undefined"
    ) {
        return "";
    }

    return String(value).replace(",", ".");
};

const parseDecimalValue = (value) => {
    if (value === "" || value === null || value === undefined) {
        return null;
    }

    const parsedValue = Number(String(value).replace(",", "."));
    return Number.isNaN(parsedValue) ? null : parsedValue;
};

const hasValue = (value) => value !== "" && value !== null && value !== undefined;

const Gordulo = () => {
    const { order, updateOrder } = useOrder();
    const { saveOrder } = useOrderSave();
    const navigate = useStepNavigate();

    const [balGordulo, setBalGordulo] = useState(
        hasValue(formatDecimalInput(order.hmeliorder?.hmeliorder_rolling_left))
    );
    const [balMeret, setBalMeret] = useState(formatDecimalInput(order.hmeliorder?.hmeliorder_rolling_left));
    const [balSarokMeret, setBalSarokMeret] = useState(
        formatDecimalInput(order.hmeliorder?.hmeliorder_cornerlift_left)
    );

    const [jobbGordulo, setJobbGordulo] = useState(
        hasValue(formatDecimalInput(order.hmeliorder?.hmeliorder_rolling_right))
    );
    const [jobbMeret, setJobbMeret] = useState(formatDecimalInput(order.hmeliorder?.hmeliorder_rolling_right));
    const [jobbSarokMeret, setJobbSarokMeret] = useState(
        formatDecimalInput(order.hmeliorder?.hmeliorder_cornerlift_right)
    );

    const [loading, setLoading] = useState(false);

    const patchOrder = (changes) => {
        updateOrder({
            ...order,
            hmeliorder: {
                ...order.hmeliorder,
                ...changes,
            },
        });
    };

    const handleSelect = (side) => {
        if (side === "bal") {
            const next = !balGordulo;
            setBalGordulo(next);
            patchOrder({ hmeliorder_rolling_left: next ? parseDecimalValue(balMeret) : null });
            if (!next) setBalMeret("");
        } else {
            const next = !jobbGordulo;
            setJobbGordulo(next);
            patchOrder({ hmeliorder_rolling_right: next ? parseDecimalValue(jobbMeret) : null });
            if (!next) setJobbMeret("");
        }
    };

    const handleInput = (side, value) => {
        if (!decimalPattern.test(value)) return;

        const normalizedValue = value.replace(",", ".");
        const parsedValue = parseDecimalValue(normalizedValue);

        if (side === "bal") {
            setBalMeret(normalizedValue);
            if (balGordulo) patchOrder({ hmeliorder_rolling_left: parsedValue });
        } else {
            setJobbMeret(normalizedValue);
            if (jobbGordulo) patchOrder({ hmeliorder_rolling_right: parsedValue });
        }
    };

    const handleInputSarok = (side, value) => {
        if (!decimalPattern.test(value)) return;
        if (side === "bal") {
            setBalSarokMeret(value);
            patchOrder({ hmeliorder_cornerlift_left: value });
        } else {
            setJobbSarokMeret(value);
            patchOrder({ hmeliorder_cornerlift_right: value });
        }
    };

    const handleNext = async () => {
        try {
            setLoading(true);

            const updatedOrder = {
                ...order,
                hmeliorder: {
                    ...order.hmeliorder,
                    hmeliorder_rolling_left: balGordulo ? parseDecimalValue(balMeret) : null,
                    hmeliorder_rolling_right: jobbGordulo ? parseDecimalValue(jobbMeret) : null,
                    hmeliorder_cornerlift_left: balSarokMeret || null,
                    hmeliorder_cornerlift_right: jobbSarokMeret || null
                },
            };

            updateOrder(updatedOrder);

            const result = await saveOrder(updatedOrder);
            if (result.success) {
                showSuccessToast("A rendelés sikeresen mentve!");
                if (order.hmeliorder?.hmeliorder_depot_prefix === "BETÉT") {
                    navigate('/CsatolandoMedia');
                } else {
                    navigate('/BetetTipus');
                }
            } else {
                showErrorToast("Hiba történt a rendelés mentésekor!");
            }
        } catch (error) {
            console.error("Hiba a rendelés mentésekor:", error);
            showErrorToast("Hiba történt a rendelés mentésekor!");
        } finally {
            setLoading(false);
        }
    };

    const canProceed =
         (!balGordulo || hasValue(balMeret)) &&
        (!jobbGordulo || hasValue(jobbMeret));

    const gombStilus = (selected) =>
        `btn w-100 py-4 px-3 shadow-sm rounded-4 d-flex flex-column align-items-center fw-bold ${selected ? "btn-primary" : "btn-outline-primary"}`;

    return (
        <TablerLayout title="Adja meg a gördülő beállításait!">
            <div className="row justify-content-center">
                <div className="col-md-10">
                    <div className="card-body">
                        <div className="row g-4">
                            {/* Bal oldal */}
                            <div className="col-12 col-md-6">
                                <div className="fw-bold mb-3 text-center fs-5">Bal oldal</div>
                                <div className="mb-3">
                                    <button
                                        type="button"
                                        className={gombStilus(balGordulo)}
                                        onClick={() => handleSelect("bal")}
                                    >
                                        Bal gördülő
                                    </button>
                                </div>
                                <div className="mb-3">
                                    <FloatingLabelInput
                                        label="Bal méret (milliméterben)"
                                        id="balGorduloMeretInput"
                                        name="balGorduloMeret"
                                        type="number"
                                        min={0}
                                        max={99}
                                        value={balMeret}
                                        onChange={e => handleInput("bal", e.target.value)}
                                        required={balGordulo}
                                        disabled={!balGordulo}
                                        labelClassName="fs-5 fw-bold text-primary"
                                        addon="mm"
                                        fontSize={25}
                                    />
                                </div>
                                <div className="mb-3">
                                    <button
                                        type="button"
                                        className={`btn w-100 py-4 px-3 shadow-sm rounded-4 fw-bold ${!balGordulo ? "btn-secondary" : "btn-outline-secondary"}`}
                                        onClick={() => handleSelect("bal")}
                                    >
                                        Nem kell
                                    </button>
                                </div>
                                <div className="fw-bold mb-3 text-center fs-5">Bal sarok emelés</div>
                                <div className="mb-3">
                                    <FloatingLabelInput
                                        label="Bal sarok emelés (milliméterben)"
                                        id="balSarokMeretInput"
                                        name="balSarokMeret"
                                        type="number"
                                        min={0}
                                        max={99}
                                        value={balSarokMeret}
                                        onChange={(e) => handleInputSarok("bal", e.target.value)}
                                        labelClassName="fs-5 fw-bold text-primary"
                                        addon="mm"
                                        fontSize={25}
                                    />
                                </div>
                            </div>

                            {/* Jobb oldal */}
                            <div className="col-12 col-md-6">
                                <div className="fw-bold mb-3 text-center fs-5">Jobb oldal</div>
                                <div className="mb-3">
                                    <button
                                        type="button"
                                        className={gombStilus(jobbGordulo)}
                                        onClick={() => handleSelect("jobb")}
                                    >
                                        Jobb gördülő
                                    </button>
                                </div>
                                <div className="mb-3">
                                    <FloatingLabelInput
                                        label="Jobb méret (milliméterben)"
                                        id="jobbGorduloMeretInput"
                                        name="jobbGorduloMeret"
                                        type="number"
                                        min={0}
                                        max={99}
                                        value={jobbMeret}
                                        onChange={(e) => handleInput("jobb", e.target.value)}
                                        required={jobbGordulo}
                                        disabled={!jobbGordulo}
                                        labelClassName="fs-5 fw-bold text-primary"
                                        addon="mm"
                                        fontSize={25}
                                    />
                                </div>
                                <div className="mb-3">
                                    <button
                                        type="button"
                                        className={`btn w-100 py-4 px-3 shadow-sm rounded-4 fw-bold ${!jobbGordulo ? "btn-secondary" : "btn-outline-secondary"}`}
                                        onClick={() => handleSelect("jobb")}
                                    >
                                        Nem kell
                                    </button>
                                </div>
                                 <div className="fw-bold mb-3 text-center fs-5">Jobb sarok emelés</div>
                                <div className="mb-3">
                                    <FloatingLabelInput
                                        label="Jobb sarok emelés (milliméterben)"
                                        id="jobbSarokMeretInput"
                                        name="jobbSarokMeret"
                                        type="number"
                                        min={0}
                                        max={99}
                                        value={jobbSarokMeret}
                                        onChange={(e) => handleInputSarok("jobb", e.target.value)}
                                        labelClassName="fs-5 fw-bold text-primary"
                                        addon="mm"
                                        fontSize={25}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="card-footer d-flex justify-content-between">
                        <StepperNavButton
                            side="left"
                            label="Vissza"
                            onClick={() => navigate('/Donga')}
                        />
                        <StepperNavButton
                            side="right"
                            label="Tovább"
                            onClick={handleNext}
                            disabled={!canProceed || loading}
                        />
                    </div>
                </div>
            </div>
        </TablerLayout>
    );
};

export default Gordulo;